import { queryClient } from "@app/app/services/queryClient";
import type { IAvailability, ICalendarAvailability } from "@app/availability/types";
import type { IUseGetOptions, IUsePrefetchGetOptions } from "@app/common/types";
import { addMonths, endOfMonth, format, isBefore, max, min, parseISO, startOfDay, startOfMonth } from "date-fns";
import { useMemo } from "react";
import { useQueries, useQuery, UseQueryResult } from "react-query";

import { QueryKeys } from "./constants/QueryKeys";
import { checkAvailability, getAvailability, getCalendarAvailability } from "./queries";
import { IAvailabilityCheck } from "./types";

/**
 * useGetAvailability
 */
export const useGetAvailability = (propertyId: string | undefined, searchQuery: string, options?: IUseGetOptions) =>
    useQuery<IAvailability[], Error>(
        [QueryKeys.AvailabilityItems, propertyId, searchQuery],
        () => getAvailability(propertyId || "", searchQuery),
        options
    );

/**
 * useCheckAvailability
 */
export const useCheckAvailability = (propertyId: string | undefined, searchQuery: string, options?: IUseGetOptions) =>
    useQuery<IAvailabilityCheck, Error>(
        [QueryKeys.AvailabilityCheck, searchQuery],
        () => checkAvailability(propertyId || "", searchQuery),
        options
    );

export const useGetCalendarAvailability = (
    propertyId: string,
    fromDate: string | undefined,
    toDate: string | undefined,
    activeCalendarDate: Date,
    options?: IUseGetOptions
) => {
    // get all months we need to fetch
    const months = useGetCalendarSlidingDateRanges(fromDate, toDate, activeCalendarDate);

    const queries = useQueries(
        months.map((month) => ({
            queryKey: [QueryKeys.CalendarAvailabilityItems, propertyId, month],
            queryFn: () => getCalendarAvailability(propertyId, month.fromDate, month.toDate),
            staleTime: 1000 * 30, // 30 seconds
            ...options,
        }))
    ) as Array<UseQueryResult<ICalendarAvailability, Error>>;

    const data = useMemo(() => queries.reduce((acc, query) => ({ ...acc, ...query.data }), {} as ICalendarAvailability), [queries]);

    return {
        data,
        isLoading: queries.some((query) => query.isLoading),
    };
};

export const prefetchCalendarAvailability = (propertyId: string, month: Date, options?: IUsePrefetchGetOptions) => {
    const today = startOfDay(new Date());
    const range = {
        fromDate: format(max([month, today]), "yyyy-MM-dd"),
        toDate: format(endOfMonth(month), "yyyy-MM-dd"),
    };
    queryClient.prefetchQuery(
        [QueryKeys.CalendarAvailabilityItems, propertyId, range],
        () => getCalendarAvailability(propertyId, range.fromDate, range.toDate),
        options
    );
};

export const CALENDAR_BUFFER_MONTHS = 2;

const useGetCalendarSlidingDateRanges = (fromDate: string | undefined, toDate: string | undefined, activeCalendarDate: Date) =>
    useMemo(() => {
        const today = startOfDay(new Date());
        const activeMonthStart = startOfMonth(activeCalendarDate);

        // Calculate start month (min of activeMonth or fromDate, clamped with this month)
        const startMonth = startOfMonth(max([today, fromDate ? min([activeMonthStart, parseISO(fromDate)]) : activeMonthStart]));

        // Calculate end month (max of activeMonth + 1 month or toDate)
        const endMonth = endOfMonth(
            toDate
                ? max([parseISO(toDate), addMonths(activeMonthStart, CALENDAR_BUFFER_MONTHS)])
                : addMonths(activeMonthStart, CALENDAR_BUFFER_MONTHS)
        );

        const result: Array<{ fromDate: string; toDate: string }> = [];
        let currentMonth = startMonth;

        // create ranges to fetch
        while (isBefore(currentMonth, endMonth)) {
            result.push({
                fromDate: format(max([currentMonth, today]), "yyyy-MM-dd"),
                toDate: format(endOfMonth(currentMonth), "yyyy-MM-dd"),
            });
            currentMonth = addMonths(currentMonth, 1);
        }

        return result;
    }, [fromDate, toDate, activeCalendarDate]);
